import {
    AggregationFunction,
    AnalyticsProjection,
    AnalyticsRequest,
} from '@sprinklr/stories/analytics/AnalyticsRequest';
import DataSet from '@sprinklr/stories/analytics/DataSet';
import Metric from '@sprinklr/stories/analytics/Metric';
import { FieldDataType, FieldType, SpaceRequest } from '@sprinklr/stories/reporting/types';

export function getProjectionOrigOrderFromRequest(
    request: SpaceRequest,
    origRequest: AnalyticsRequest
): number[] {
    // origOrder is lost for projectionDecorations, so we need to find the origOrder from the original request
    if (request.projectionDecorations?.length > 0) {
        return request.projectionDecorations.map(projection => {
            return origRequest.projections.findIndex(proj => projection === proj.aggregateFunction);
        });
    }
    // account for projection nested in groupBy
    const orderAdditionalGroupBy = request.groupBys?.[0]?.projections.map(projection => {
        return projection.details.origOrder;
    });

    const orderAdditional = request.projections?.map(projection => {
        return projection.details.origOrder;
    });

    return orderAdditionalGroupBy?.length > 0 ? orderAdditionalGroupBy : orderAdditional;
}

export function ensureProjectionOrder(
    dataSets: DataSet[],
    origRequest: AnalyticsRequest
): DataSet[] {
    const origMetrics: Metric[] = [];
    origRequest.projections?.forEach(projection => {
        const type = getType(projection.details?.dataType);
        const dataType = getDataType(projection.details?.dataType, projection.aggregateFunction);

        const metricHeading = getHeading(projection, origMetrics);

        origMetrics.push(
            new Metric(
                metricHeading,
                type,
                dataType,
                projection.aggregateFunction,
                !!projection.details?.alternateHeading
            )
        );
    });

    const data =
        origMetrics.length > 1
            ? dataSets.map(dataSet => {
                  return dataSet.reorderMetrics(origMetrics);
              })
            : dataSets;

    return data;
}

export function getHeading(projection: AnalyticsProjection, origMetrics: Metric[]): string {
    const altHeading =
        projection?.details?.alternateHeading && projection?.details?.alternateHeading.length
            ? projection.details.alternateHeading
            : null;

    const label = projection?.details?.label;
    let metricHeading = altHeading ?? label ?? projection?.heading;

    if (origMetrics.find(metric => metric.name === metricHeading)) {
        metricHeading = `${metricHeading}_${projection?.aggregateFunction}`;
    }
    return metricHeading;
}

export function getType(dataType: FieldDataType): FieldType {
    let type: FieldType = 'NUMBER';
    if (dataType === 'TIME_DIFFERENCE') {
        type = 'TIME_INTERVAL';
    }
    if (dataType === 'DATETIME') {
        type = 'TIMESTAMP';
    }
    return type;
}

export function getDataType(
    dataType: FieldDataType,
    aggregateFunction: AggregationFunction
): FieldDataType {
    let type: FieldDataType = dataType;
    const percentageBasedAggregateFunction = ['PERCENTAGE_CHANGE', 'PERCENTAGE'];
    if (percentageBasedAggregateFunction.includes(aggregateFunction)) {
        type = 'PERCENTAGE';
    }
    return type;
}
